/**
 * Sub plans.
 *
 * Written for the person standing in the doorway at 7:40 with no idea where
 * the pencils are. Two rules run through the whole shape of this:
 *
 *  1. **No times are stored here.** A block points at a lesson-plan row, and
 *     the times are read off that row when the plan is shown. Move Reading to
 *     nine o'clock in Lesson Plans and the sub plan has moved too — there is
 *     no second copy of the schedule to fall out of step.
 *
 *  2. **Everything is editable.** The built-in plans are seeded into the
 *     teacher's own documents on first use rather than being read out of the
 *     code at render time, so she can rewrite any line of them.
 */
export const SUB_TIERS = ['boy', 'moy', 'eoy'];
/**
 * The fourth tier, which is not a time of year at all.
 *
 * Plans the teacher wrote herself live under `custom`. They are the same shape
 * as the shipped ones and go through the same routes; what marks them out is
 * that nothing seeds them, nothing resets them, and she names them herself —
 * "Flu week", "Half day", "The day of the assembly".
 *
 * The `day` number on a custom plan is a slot rather than a day of an absence.
 * That reads oddly and is worth saying plainly: it is what lets custom plans
 * share the existing one-plan-per-(teacher, tier, day) index instead of
 * needing that index rebuilt underneath a database with a year of work in it.
 */
export const CUSTOM_TIER = 'custom';
/** True for a plan she wrote, false for one of the nine shipped ones. */
export function isCustomPlan(tier) {
    return tier === CUSTOM_TIER;
}
/**
 * Three days per tier, not one.
 *
 * One day covers being sent home at lunchtime. It does not cover the flu,
 * which is the absence that actually happens — and a sub handed the same day
 * three times running has twenty-five children who have already done it.
 */
export const SUB_DAYS = [1, 2, 3];
export const SUB_TIER_LABELS = {
    boy: 'Beginning of year',
    moy: 'Middle of year',
    eoy: 'End of year',
};
export const SUB_TIER_MONTHS = {
    boy: 'August – November',
    moy: 'December – February',
    eoy: 'March – June',
};
export const SUB_TIER_BLURBS = {
    boy: 'Routines are still being learned. Everything is modelled first, and the work is short.',
    moy: 'They can read simple text and write a sentence. The work has more independence in it.',
    eoy: 'Close to second grade. Longer text, two-digit numbers, and writing that runs to a paragraph.',
};
/* ------------------------------------------------------------ matching */
/**
 * The row a block belongs on, when the one it was pinned to has gone.
 *
 * This is the fix for the thing that actually broke. A block stores a row
 * *id*, and ids are per-week: rebuild your schedule in September and every id
 * changes, so every sub plan written in August comes unpinned at once. The
 * plans were still there, still full of writing, and sitting in a heap at the
 * bottom with no times against them.
 *
 * So a lost block is matched again by what it is called. Names are what
 * survive a rebuilt schedule — "Phonics" is still "Phonics" — and they are
 * what a teacher would use to do this by hand.
 *
 * Deliberately not clever. Exact name, then one containing the other, then
 * nothing. No fuzzy distance, no stemming: a wrong guess here puts a reading
 * lesson in a maths slot, and the sub follows it. Better to leave a block
 * unmatched and visibly uncovered than to place it confidently and wrongly.
 */
export function matchRowByName(label, rows, taken) {
    const wanted = label.trim().toLowerCase();
    if (!wanted)
        return null;
    const free = rows.filter((row) => !taken.has(row.id));
    const exact = free.find((row) => row.label.trim().toLowerCase() === wanted);
    if (exact)
        return exact;
    /*
     * One containing the other, longest first.
     *
     * "Reading" should find "Reading Lesson", and a schedule listing both
     * "Reading Lesson" and "Reading Centers" should give the block the one whose
     * name is closest in length rather than whichever happens to sort first.
     */
    const contains = free
        .filter((row) => {
        const other = row.label.trim().toLowerCase();
        return other.includes(wanted) || wanted.includes(other);
    })
        .sort((a, b) => Math.abs(a.label.trim().length - wanted.length) -
        Math.abs(b.label.trim().length - wanted.length));
    return contains[0] ?? null;
}
export const WORKSHEET_PART_KINDS = [
    'text',
    'handwriting',
    'lines',
    'boxes',
    'problems',
    'wordbank',
    'drawbox',
    'numberline',
    'tenframe',
    'checklist',
    'table',
    'matching',
    'circleit',
    'sort',
    'cutandpaste',
    'pagebreak',
];
/**
 * The tidy-up done once, on save.
 *
 * Lists are typed into a textarea, one per line, and while she is typing there
 * are blank lines in them — the one she just made with Enter, the one left
 * behind by a word she deleted. Stripping those as she types is what broke the
 * editor: it deleted the empty line the instant Enter created it, so a list
 * could never gain a fourth item. So they are left alone until she saves, and
 * cleaned up here.
 */
export function tidyWorksheetParts(parts) {
    const clean = (values) => values.map((v) => v.trim()).filter((v) => v.length > 0);
    return parts.map((part) => {
        switch (part.kind) {
            case 'checklist':
                return { ...part, items: clean(part.items) };
            case 'wordbank':
                return { ...part, words: clean(part.words) };
            case 'problems':
                return { ...part, problems: clean(part.problems) };
            case 'matching':
                return { ...part, left: clean(part.left), right: clean(part.right) };
            case 'sort':
                return { ...part, columns: clean(part.columns) };
            case 'table':
                return { ...part, headers: clean(part.headers) };
            case 'cutandpaste':
                return { ...part, items: clean(part.items) };
            case 'handwriting':
                return part.trace ? { ...part, trace: clean(part.trace) } : part;
            default:
                return part;
        }
    });
}
export const SUB_FILE_RULES = {
    /**
     * What a sub is actually handed by a school: procedures as PDFs, a Word
     * document from the office, a photo of the fire route.
     *
     * SVG is not on the list, and never should be — it is a script container
     * that would be served from our own origin. Everything here is either sent
     * with `Content-Disposition: attachment` or re-encoded as an image.
     */
    acceptedMimeTypes: [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'image/jpeg',
        'image/png',
        'image/webp',
        'image/heic',
        'image/heif',
    ],
    maxUploadBytes: 10 * 1024 * 1024,
    maxPerTeacher: 40,
};
export function isImageAttachment(contentType) {
    return !!contentType && contentType.startsWith('image/');
}
/* ---------------------------------------------------------------- times */
/** '8:45' or '08:45' to minutes past midnight, school-day am/pm assumed. */
export function parseClock(value) {
    if (!value)
        return null;
    const match = /^(\d{1,2}):(\d{2})\s*(am|pm)?$/i.exec(value.trim());
    if (!match)
        return null;
    let hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour > 23 || minute > 59)
        return null;
    const suffix = match[3]?.toLowerCase();
    if (suffix === 'pm' && hour < 12)
        hour += 12;
    if (suffix === 'am' && hour === 12)
        hour = 0;
    /*
     * No suffix, and a small hour. A first-grade day does not start at one in
     * the morning, so 1:00 is the afternoon — which is what a teacher means
     * when she types it, and getting this wrong turns a 45-minute block into a
     * negative number.
     */
    if (!suffix && hour < 7)
        hour += 12;
    return hour * 60 + minute;
}
/** Whole minutes between two clock strings, or null if either is unreadable. */
export function blockMinutes(start, end) {
    const from = parseClock(start);
    const to = parseClock(end);
    if (from == null || to == null)
        return null;
    const span = to - from;
    return span > 0 ? span : null;
}
/** '8:45–9:45 · 60 min', for the line under a block heading. */
export function blockTimeLabel(start, end) {
    if (!start && !end)
        return 'No time set';
    if (!end)
        return start;
    const minutes = blockMinutes(start, end);
    return `${start ?? '?'}–${end}${minutes ? ` · ${minutes} min` : ''}`;
}
/** Which tier suits a date, so the page can open on the likely one. */
export function tierForDate(date) {
    const month = date.getMonth();
    if (month >= 7 && month <= 10)
        return 'boy'; // Aug–Nov
    if (month === 11 || month <= 1)
        return 'moy'; // Dec–Feb
    return 'eoy'; // Mar–Jul
}
//# sourceMappingURL=subPlans.js.map